import { forwardRef, ReactNode, Ref } from 'react';
import cn from 'classnames';

import { Card } from '../card/Card';
import { Heading } from '../../atoms/heading/Heading';
import { Radio, RadioRoot } from '../../../components/molecules/radio/Radio';
import { Text } from '../../../components/atoms/text/Text';

import './radio-group.scss';

export interface RadioGroupOption {
  /**
   * The name of the option
   * @example Trial Defense
   */
  name?: string;
  /**
   * The price of the option
   * @example 14.95
   */
  price?: number;
  /**
   * The value of the option
   * @example trial-defense
   */
  value: string;
  /**
   * JSX to render in the option
   * @note This overwrites the `name`, `price`, and `value` props.
   */
  content?: ReactNode;
}

export interface RadioGroupProps extends Omit<React.HTMLProps<HTMLDivElement>, 'onClick' | 'css'> {
  /**
   * The content of the component.
   */
  children?: ReactNode;
  /**
   * An event to handle option selection.
   */
  onClick?: (option: RadioGroupOption) => void;
  /**
   * A list of options for RadioGroup.
   * @note This will overwrite any children passed to the component.
   */
  options?: RadioGroupOption[];
  /**
   * title props to show label.
   */
  title?: string;
  /**
   * Value props to show selected option
   */
  value?: string;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
}

const RadioGroupComponent = (
  { children, title, options, value, onClick, classNames = [], ...props }: RadioGroupProps,
  ref: RadioGroupProps['ref'],
) => {
  const classRadioGroup = 'lsux-radio-group';
  const classOption = `${classRadioGroup}-option`;

  return (
    <div {...props} className={cn(`${classRadioGroup}-container`, classNames)} ref={ref}>
      {title && <Heading as="T20" text={title} classNames={['mb-4']} />}
      <Card>
        <ul className={`${classRadioGroup}-options`}>
          <RadioRoot value={value}>
            {options?.length > 0
              ? options.map((option, index) => (
                  <li className={`${classOption}__li`} key={option.name} onClick={() => onClick && onClick(option)}>
                    <div className={`${classOption}__div`}>
                      <label htmlFor={'helper-radio-' + index} className={`${classOption}__div__label`}>
                        {option.content ? (
                          option.content
                        ) : (
                          <>
                            {option.name && <Text textWeight="normal" text={option.name} classNames={['mb-1']} />}
                            {option.price && <Text textWeight="semibold" text={`$${option.price}/mo`} />}
                          </>
                        )}
                      </label>
                      <Radio value={option.value} id={'helper-radio-' + index} />
                    </div>
                  </li>
                ))
              : children}
          </RadioRoot>
        </ul>
      </Card>
    </div>
  );
};

/**
 * A radio button is a quick way to extract a single coded answer from our users by presenting multiple possibilities and allowing only one option to be chosen.
 */

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(RadioGroupComponent);
